/**
 * The "Quality Audits" board, read as data.
 *
 * One definition of what a punch is, what an audit is, which of them the report covers and
 * when one is open — used by the snapshot job that writes the rows and, later, by every
 * report that reads them. Pure: no database, no clock, no Monday client.
 *
 * ⚠ **"Quality HCA" is not a board.** It is the only group (`quality_hca`) of the board
 * *Quality Audits*, id 1786804896, in the Operations workspace on unifiber-be.monday.com.
 * The meeting calls the group by name, which reads like a board and is not one.
 *
 * ⚠ **Columns are addressed by id, never by title.** A title is editable by anyone with the
 * board open; a lookup by title breaks silently the day somebody renames one, and a report
 * that silently counts zero is worse than one that fails.
 *
 * ⚠ **Labels are matched character for character.** `CIF - Circet` with its spaces and
 * capitals is the string the board holds. A label that differs by one capital counts zero,
 * silently — which is also why nothing here normalises, trims or lower-cases a label before
 * storing it.
 *
 * ⚠ **Monday's own `done_colors` are ignored.** On this board they flag `NOK` and
 * `CIF - Circet` as "done". Open and closed are defined here, explicitly.
 */
// ─────────────────────────────────────────────────────────────────────────────
// The board
// ─────────────────────────────────────────────────────────────────────────────
/** Board *Quality Audits*, workspace Operations. Measured 17 Sep 2026: 5 880 items. */
export const QUALITY_AUDITS_BOARD_ID = "1786804896";
/** The group the meeting calls "Quality HCA". Carried for recognition, not for filtering. */
export const QUALITY_HCA_GROUP_ID = "quality_hca";
/**
 * Every column the snapshot reads, by id.
 *
 * Measured on the board 17 Sep 2026. Adding one here is all that is needed to carry it
 * into the snapshot: the read, the raw record and the tests all enumerate this object.
 */
export const COLUMNS = {
    priority: "dropdown_mky9mycm",
    statusUnifiber: "status_mkmm9ce",
    statusContractor: "status_mkmmjfn",
    /** The partner — "Build contractor" on the board. What row scoping matches on. */
    buildContractor: "color_mkp6fe43",
    /** The geographic dimension that matters. POP is secondary and often blank. */
    area: "dropdown_mky0st0r",
    laxonName: "text_mkmf3m43",
    popName: "text_mm0madk",
    /**
     * ⚠ THE ADDRESS. The item's own NAME is not one: measured 22 Sep 2026, all 684
     * dispatched audits are named with a 32-character hex identifier from the system that
     * feeds them, and the punches carry a work reference (`CHAR007`, `10081381`). The street
     * lives in these two columns instead.
     *
     * Coverage, same measurement: `STREET_NM` on 684/684 audits and 901/927 punches;
     * `HNR_FULL` on 502 and 848. So the street is almost always known and the house number
     * often is not — which is why `addressOf` composes what it has rather than assuming both.
     */
    streetName: "text_mkmfqer8",
    houseNumber: "text_mkmf2dcf",
    dispatchDate: "date_1_mkmmx0dd",
    doneDate: "date_2_mkmmc142",
    validatedDate: "date_3_mkmm8w6t",
    /** Only a VLS/FO observation carries this. It is what tells a punch from an audit. */
    typeVlsFo: "dropdown_mkxfd49d",
    vlsFoStatus: "color_mkxfv9w9",
    finaleOkForHc: "color_mkpt79vm",
};
/** What the Monday client asks for. Order is irrelevant; completeness is not. */
export const SNAPSHOT_COLUMN_IDS = Object.values(COLUMNS);
// ─────────────────────────────────────────────────────────────────────────────
// Labels, exactly as the board spells them
// ─────────────────────────────────────────────────────────────────────────────
/** Unifiber's own side of the workflow. */
export const STATUS_UNIFIBER = {
    DISPATCHED: "Dispatched",
    REDO: "Redo",
    VALIDATION_PLANNED: "Validation planned",
    VALIDATED: "Validated",
};
/** The contractor's side. `Done` is the contractor's claim, not a closure — see `isOpen`. */
export const STATUS_CONTRACTOR = {
    ACCEPTED: "Accepted",
    PLANNED: "Planned",
    REFUSED: "Refused",
    DONE: "Done",
};
export const PRIORITIES = ["P1", "P2", "P3"];
export function isPriority(value) {
    return value !== null && PRIORITIES.includes(value);
}
/**
 * One column's text, or null when it is unset.
 *
 * ⚠ Monday returns `""` for an unset column, not null. Collapsing the two here means every
 * caller tests one thing, and stops `""` being counted as a partner, a commune or a
 * priority in its own right.
 */
export function textOf(item, columnId) {
    const value = item.column_values.find((c) => c.id === columnId)?.text;
    return value === null || value === undefined || value === "" ? null : value;
}
/**
 * A board date column's text as `YYYY-MM-DD`, or null.
 *
 * Monday renders a date column as `2026-09-17`, and as `2026-09-17 14:30` when the column
 * carries a time. Only the day is kept: every deadline in this product is counted in whole
 * days from the dispatch date, and a time would invite an age that depends on the hour.
 * Anything that is not a plausible date is null rather than a guess.
 */
export function parseBoardDate(text) {
    if (text === null)
        return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text.trim());
    if (!match)
        return null;
    const [, year, month, day] = match;
    const monthNum = Number(month);
    const dayNum = Number(day);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31)
        return null;
    return `${year}-${month}-${day}`;
}
/**
 * Punch or audit.
 *
 * A VLS/FO observation is the only kind that carries `Type VLS/FO`; a priority is accepted
 * as evidence too, so an observation whose type was left blank is still counted as the
 * punch it is rather than filed among 4 959 audits.
 */
export function kindOf(item) {
    const isPunch = textOf(item, COLUMNS.typeVlsFo) !== null || textOf(item, COLUMNS.priority) !== null;
    return isPunch ? "PUNCH" : "AUDIT";
}
/**
 * Is this item still open?
 *
 * ⚠ **Only `Validated` closes an item** (Jérémy, 17 Sep 2026). An item the contractor
 * marked `Done` is a claim awaiting Réginald's approval, and counting it as closed would
 * let a contractor close their own punches. An item with no `Status Unifiber` at all is
 * open: nothing has been decided about it yet.
 */
export function isOpen(statusUnifiber) {
    return statusUnifiber !== STATUS_UNIFIBER.VALIDATED;
}
/**
 * Is this item in the report's scope?
 *
 * ⚠ The scope decided on 17 Sep 2026: the VLS/FO punches — all of them, priority or not —
 * **and** the HCA audits that entered the dispatch workflow, which is to say the ones that
 * carry a `Status Unifiber` (~680 of 4 959). A filter decides later whether the audits are
 * shown; the snapshot keeps both so the filter has something to decide about.
 */
export function inScope(row) {
    return row.kind === "PUNCH" || row.statusUnifiber !== null;
}
/** One item → one row. Nothing is normalised, trimmed or re-cased on the way through. */
export function toRow(item) {
    const raw = {};
    for (const columnId of SNAPSHOT_COLUMN_IDS)
        raw[columnId] = textOf(item, columnId);
    const statusUnifiber = textOf(item, COLUMNS.statusUnifiber);
    return {
        mondayItemId: item.id,
        name: item.name,
        groupTitle: item.group?.title ?? null,
        createdAt: item.created_at ?? null,
        kind: kindOf(item),
        priority: textOf(item, COLUMNS.priority),
        statusUnifiber,
        statusContractor: textOf(item, COLUMNS.statusContractor),
        buildContractor: textOf(item, COLUMNS.buildContractor),
        area: textOf(item, COLUMNS.area),
        laxonName: textOf(item, COLUMNS.laxonName),
        popName: textOf(item, COLUMNS.popName),
        streetName: textOf(item, COLUMNS.streetName),
        houseNumber: textOf(item, COLUMNS.houseNumber),
        dispatchDate: parseBoardDate(textOf(item, COLUMNS.dispatchDate)),
        doneDate: parseBoardDate(textOf(item, COLUMNS.doneDate)),
        validatedDate: parseBoardDate(textOf(item, COLUMNS.validatedDate)),
        isOpen: isOpen(statusUnifiber),
        raw,
    };
}
/** The whole board → the rows the snapshot stores. Order is the board's. */
export function scopedRows(items) {
    return items.map(toRow).filter(inScope);
}
/**
 * The street address, as a person would read it.
 *
 * ⚠ **Not the item's name.** The board names an audit with a 32-character hex identifier
 * and a punch with a work reference; neither is an address. Composed from what is actually
 * there: `Rue du Roi Albert 11` when both columns are filled, `Rue du Roi Albert` when only
 * the street is, and `null` when neither is — never a half-built string like `, Dour` or a
 * bare house number, which reads as a bug rather than as missing data.
 *
 * The locality is deliberately NOT appended here: the screen already shows the commune in
 * its own column, and repeating it in every address wastes the width a street name needs.
 */
export function addressOf(row) {
    if (row.streetName === null)
        return null;
    return row.houseNumber === null ? row.streetName : `${row.streetName} ${row.houseNumber}`;
}
//# sourceMappingURL=quality-audits.js.map